import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Link, useBeforeUnload, useBlocker, useNavigate, useParams } from "react-router-dom";
import {
  FiClipboard,
  FiChevronDown,
  FiChevronUp,
  FiCrosshair,
  FiDownload,
  FiEdit3,
  FiFileText,
  FiShoppingCart,
  FiUpload,
} from "react-icons/fi";
import { BASE_URL, URL } from "../../redux/constants";
import { setCartItem } from "../../redux/features/cartSlice";
import {
  useAiRematchBomMutation,
  useBuildBomCartItemsMutation,
  useCreateBlankBomMutation,
  useCreateClipboardBomMutation,
  useCreateManualBomMutation,
  useCreateBomItemMutation,
  useDeleteBomItemsMutation,
  useGetBomOrderQuery,
  useGetBomRelatedOrdersQuery,
  usePreviewBomPurchaseMutation,
  useReparseBomMutation,
  useRematchBomMutation,
  useReviewBomAiSuggestionMutation,
  useSearchBomItemCandidatesMutation,
  useUpdateBomItemMutation,
  useUpdateBomItemsMutation,
  useUploadBomMutation,
} from "../../redux/api/bomApi";
import UniverBomSheet, {
  BOM_SHEET_COLUMNS,
  MATCH_STATUS,
  STATUS_LABELS,
  buildBomRawColumns,
  extractBomSheetRows,
  formatMoney,
  getSelectedBomItemIds,
} from "./UniverBomSheet";
import BomHistoryPanel from "./BomHistoryPanel";
import {
  createBomResponseError,
  getBomRequestErrorDetails,
  isBomWriteConflict,
} from "./bomRequestError";
import {
  buildPurchaseSetsPreview,
  resolvePurchaseSetsForBom,
} from "./bomPurchaseSets";
import "./BomOrderPage.css";

/**
 * 获取 BOM 本地会话 ID，游客上传时用于访问自己的 BOM 任务。
 * @returns {string} BOM 会话 ID
 */
const getBomSessionId = () => {
  const storageKey = "bomOrderSessionId";
  const existingId = window.localStorage.getItem(storageKey);
  if (existingId) return existingId;

  const nextId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  window.localStorage.setItem(storageKey, nextId);
  return nextId;
};

/**
 * 判断 BOM 明细是否允许加入购物车。
 * @param {Object} item BOM 明细
 * @returns {boolean} 是否允许选择
 */
const canSelectBomItem = (item = {}) => {
  return item.matchStatus === "matched" && item.selectedProduct && !item.needQuote;
};

/**
 * 计算 BOM 完全匹配率。
 * @param {Object} summary BOM 汇总信息
 * @returns {number} 匹配率
 */
const getMatchRate = (summary = {}) => {
  const totalRows = Number(summary.totalRows || 0);
  if (!totalRows) return 0;
  return Math.round((Number(summary.matchedRows || 0) / totalRows) * 1000) / 10;
};

/**
 * 获取标准 BOM 模板下载地址。
 * @returns {string} 模板接口地址
 */
const getBomTemplateUrl = () => `${BASE_URL || ""}${URL}/bom/template`;

const BOM_COLUMN_PREFERENCES_KEY = "bomOrderColumnPreferences";
const MAX_AI_REMATCH_ROWS = 20;
const BOM_UNSAVED_CHANGES_MESSAGE = "BOM changes are still saving or failed to save. Please finish saving before leaving this page.";
const BOM_MAPPING_FIELDS = [
  { key: "originalMpn", label: "Part Number (MPN)", requiredGroup: "part" },
  { key: "originalSupplierPartNo", label: "Supplier Part Number / C-number", requiredGroup: "part" },
  { key: "unitQty", label: "Quantity", required: true },
  { key: "originalManufacturer", label: "Manufacturer / Brand" },
  { key: "packageSize", label: "Package / Size" },
  { key: "originalDescription", label: "Description / Parameters" },
  { key: "designator", label: "Designator" },
  { key: "supplier", label: "Supplier" },
  { key: "productionSurplusQty", label: "Spare Quantity" },
];

/**
 * 读取并修复 BOM 云表列偏好，兼容后续新增列。
 * @returns {{order: string[], hiddenKeys: string[]}} 列顺序和隐藏列
 */
const loadBomColumnPreferences = () => {
  const defaultOrder = BOM_SHEET_COLUMNS.map((column) => column.key);
  if (typeof window === "undefined") return { order: defaultOrder, hiddenKeys: [] };

  try {
    const saved = JSON.parse(window.localStorage.getItem(BOM_COLUMN_PREFERENCES_KEY) || "{}");
    const savedOrder = Array.isArray(saved.order) ? saved.order : [];
    const order = Array.from(new Set(savedOrder.filter((key) => typeof key === "string")));
    if (order.length === 0) order.push(...defaultOrder);
    const hiddenKeys = Array.isArray(saved.hiddenKeys)
      ? Array.from(new Set(saved.hiddenKeys.filter((key) => typeof key === "string")))
      : [];

    return {
      order,
      hiddenKeys: hiddenKeys.length === defaultOrder.length ? [] : hiddenKeys,
    };
  } catch (error) {
    return { order: defaultOrder, hiddenKeys: [] };
  }
};

/**
 * 将本地列偏好与当前 BOM 的标准列、原始列合并。
 * @param {{order: string[], hiddenKeys: string[]}} preferences 本地列偏好
 * @param {Object[]} columns 当前全部列
 * @returns {{order: string[], hiddenKeys: string[]}} 当前有效列偏好
 */
const reconcileBomColumnPreferences = (preferences, columns) => {
  const validKeys = new Set(columns.map((column) => column.key));
  const savedOrder = (preferences.order || []).filter((key) => validKeys.has(key));
  const order = Array.from(new Set(savedOrder));
  columns.forEach((column) => {
    if (!order.includes(column.key)) order.push(column.key);
  });

  const hiddenKeys = new Set(
    (preferences.hiddenKeys || []).filter((key) => validKeys.has(key)),
  );
  columns.forEach((column) => {
    if (!savedOrder.includes(column.key) && column.defaultHidden) hiddenKeys.add(column.key);
  });

  // 旧列偏好中没有新字段时，将订购量放回数量列组，避免追加到宽表末尾而不易发现。
  if (!savedOrder.includes("orderQty") && order.includes("orderQty")) {
    const orderQtyIndex = order.indexOf("orderQty");
    order.splice(orderQtyIndex, 1);
    const spareQtyIndex = order.indexOf("productionSurplusQty");
    order.splice(spareQtyIndex >= 0 ? spareQtyIndex + 1 : order.length, 0, "orderQty");
  }

  return { order, hiddenKeys: Array.from(hiddenKeys) };
};

/**
 * 将 BOM 云表列偏好保存到浏览器本地。
 * @param {{order: string[], hiddenKeys: string[]}} preferences 列偏好
 */
const saveBomColumnPreferences = (preferences) => {
  window.localStorage.setItem(BOM_COLUMN_PREFERENCES_KEY, JSON.stringify(preferences));
};

const BomOrderPage = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { bomId } = useParams();
  const fileInputRef = useRef(null);
  const univerApiRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const autoSaveInFlightRef = useRef(false);
  const autoSavePromiseRef = useRef(Promise.resolve(true));
  const pendingAutoSaveIdsRef = useRef(new Set());
  const pendingAutoSavePatchesRef = useRef(new Map());
  const allowNextNavigationRef = useRef(false);
  const saveProtectionRef = useRef(null);
  const navigationBlockerRef = useRef(null);
  const purchaseSetsBomIdRef = useRef("");
  const [sessionId] = useState(getBomSessionId);
  const [bomOrder, setBomOrder] = useState(null);
  const [activeStatuses, setActiveStatuses] = useState(new Set(MATCH_STATUS));
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [purchaseSets, setPurchaseSets] = useState(1);
  const [onlySelected, setOnlySelected] = useState(false);
  const [columnPreferences, setColumnPreferences] = useState(loadBomColumnPreferences);
  const [columnDraft, setColumnDraft] = useState(null);
  const [headerMappingDraft, setHeaderMappingDraft] = useState(null);
  const [draggedColumnKey, setDraggedColumnKey] = useState("");
  const [importMode, setImportMode] = useState("upload");
  const [manualInput, setManualInput] = useState("");
  const [clipboardInput, setClipboardInput] = useState("");
  const [candidateReviewItemId, setCandidateReviewItemId] = useState("");
  const [candidateKeyword, setCandidateKeyword] = useState("");
  const [searchedCandidates, setSearchedCandidates] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [rowOperationStates, setRowOperationStates] = useState({});
  const [purchasePreview, setPurchasePreview] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rateLimitMessage, setRateLimitMessage] = useState("");
  const [purchaseSetsPreviewDialog, setPurchaseSetsPreviewDialog] = useState(null);
  const [overwriteConflictDialog, setOverwriteConflictDialog] = useState(null);
  const [isResolvingOverwriteConflict, setIsResolvingOverwriteConflict] = useState(false);
  const [isUpperAreaCollapsed, setIsUpperAreaCollapsed] = useState(false);
  const [createBlankBom, { isLoading: isCreatingBlankBom }] = useCreateBlankBomMutation();
  const [createClipboardBom, { isLoading: isCreatingClipboardBom }] = useCreateClipboardBomMutation();
  const [createManualBom, { isLoading: isCreatingManualBom }] = useCreateManualBomMutation();
  const [uploadBom, { isLoading: isUploading }] = useUploadBomMutation();
  const [createBomItem, { isLoading: isCreatingItem }] = useCreateBomItemMutation();
  const [deleteBomItems, { isLoading: isDeletingItems }] = useDeleteBomItemsMutation();
  const [reparseBom, { isLoading: isReparsing }] = useReparseBomMutation();
  const [updateBomItems] = useUpdateBomItemsMutation();
  const [applyBomPurchaseSets, { isLoading: isApplyingPurchaseSets }] = useUpdateBomItemsMutation();
  const [updateBomItem, { isLoading: isConfirmingCandidate }] = useUpdateBomItemMutation();
  const [searchBomItemCandidates, { isLoading: isSearchingCandidates }] = useSearchBomItemCandidatesMutation();
  const [rematchBom, { isLoading: isRematching }] = useRematchBomMutation();
  const [aiRematchBom, { isLoading: isAiRematching }] = useAiRematchBomMutation();
  const [reviewBomAiSuggestion, { isLoading: isReviewingAi }] = useReviewBomAiSuggestionMutation();
  const [buildBomCartItems, { isLoading: isAdding }] = useBuildBomCartItemsMutation();
  const [previewBomPurchase, { isLoading: isLoadingPurchasePreview }] = usePreviewBomPurchaseMutation();
  const {
    data: restoredBomData,
    error: restoreBomError,
    isLoading: isRestoringBom,
    refetch: refetchBomOrder,
  } = useGetBomOrderQuery(
    { id: bomId, sessionId },
    { skip: !bomId },
  );
  const {
    data: relatedOrderData,
    isFetching: isLoadingRelatedOrders,
  } = useGetBomRelatedOrdersQuery(
    { id: bomOrder?._id, sessionId },
    { skip: !isAuthenticated || !bomOrder?._id },
  );
  const relatedOrders = relatedOrderData?.orders || [];
  const isCreatingInitialBom = isUploading
    || isCreatingBlankBom
    || isCreatingManualBom
    || isCreatingClipboardBom
    || isRestoringBom;

  const summary = bomOrder?.summary || {};
  const isBomRouteTransitioning = Boolean(
    bomId && bomOrder?._id && String(bomId) !== String(bomOrder._id),
  );
  const matchRate = getMatchRate(summary);
  const aiPendingItems = useMemo(
    () => (bomOrder?.items || []).filter((item) => item.matchSource === "ai" && item.requiresConfirmation),
    [bomOrder?.items],
  );
  const localPendingItems = useMemo(
    () => (bomOrder?.items || []).filter((item) => (
      item.matchStatus === "pending_confirm"
      && item.matchSource !== "ai"
      && Array.isArray(item.candidates)
      && item.candidates.length > 0
    )),
    [bomOrder?.items],
  );
  const candidateReviewItem = useMemo(() => {
    const requestedItem = (bomOrder?.items || []).find((item) => item._id === candidateReviewItemId);
    return requestedItem || localPendingItems[0] || null;
  }, [bomOrder?.items, candidateReviewItemId, localPendingItems]);
  const visibleCandidates = searchedCandidates || candidateReviewItem?.candidates || [];
  const allColumns = useMemo(
    () => [...BOM_SHEET_COLUMNS, ...buildBomRawColumns(bomOrder)],
    [bomOrder],
  );
  const effectiveColumnPreferences = useMemo(
    () => reconcileBomColumnPreferences(columnPreferences, allColumns),
    [allColumns, columnPreferences],
  );
  const visibleColumns = useMemo(() => {
    const columnMap = new Map(allColumns.map((column) => [column.key, column]));
    const hiddenKeys = new Set(effectiveColumnPreferences.hiddenKeys);
    return effectiveColumnPreferences.order
      .map((key) => columnMap.get(key))
      .filter((column) => column && !hiddenKeys.has(column.key));
  }, [allColumns, effectiveColumnPreferences]);
  const headerMappingColumns = useMemo(() => {
    if (!headerMappingDraft) return [];
    return bomOrder?.sourceRows?.[headerMappingDraft.headerRowIndex] || [];
  }, [bomOrder?.sourceRows, headerMappingDraft]);

  const filteredItems = useMemo(() => {
    const items = bomOrder?.items || [];
    return items.filter((item) => {
      const statusMatched = activeStatuses.has(item.matchStatus);
      const selectedMatched = !onlySelected || selectedItemIds.has(item._id);
      return statusMatched && selectedMatched;
    });
  }, [activeStatuses, bomOrder?.items, onlySelected, selectedItemIds]);
  const selectedTotalAmount = useMemo(() => {
    return (bomOrder?.items || []).reduce((total, item) => {
      if (!selectedItemIds.has(item._id) || !canSelectBomItem(item)) return total;
      return total + Number(item.subtotal || 0);
    }, 0);
  }, [bomOrder?.items, selectedItemIds]);
  const selectedItemIdsKey = useMemo(
    () => Array.from(selectedItemIds).sort().join(","),
    [selectedItemIds],
  );
  const purchaseTotalAmount = purchasePreview?.summary?.totalAmount ?? selectedTotalAmount;
  const hasPurchaseBlockingIssue = Boolean(
    purchasePreview?.summary?.insufficientGroups || purchasePreview?.summary?.quoteGroups,
  );
  const rowOperationValues = Object.values(rowOperationStates);
  const hasPendingRowChanges = rowOperationValues.some((status) => (
    status === "saving" || status === "matching"
  ));
  const hasRowSaveErrors = rowOperationValues.includes("error");
  const hasUnsafePurchaseEdits = hasPendingRowChanges
    || hasRowSaveErrors
    || Boolean(overwriteConflictDialog);

  /**
   * 统一处理 BOM 请求错误，429 使用弹窗，其他错误沿用 Toast。
   * @param {Object|Error|null} error 请求错误
   * @param {string} fallbackMessage 默认错误文案
   */
  const handleBomRequestError = useCallback((error, fallbackMessage) => {
    const details = getBomRequestErrorDetails(error, fallbackMessage);
    if (details.isRateLimited) {
      setRateLimitMessage(details.message);
      return;
    }
    toast.error(details.message);
  }, []);

  /**
   * 在刷新或关闭浏览器前提示用户仍有未安全保存的 BOM 修改。
   * @param {BeforeUnloadEvent} event 浏览器卸载事件
   */
  const handleBeforeUnload = useCallback((event) => {
    if (!hasUnsafePurchaseEdits) return;
    event.preventDefault();
    event.returnValue = "";
  }, [hasUnsafePurchaseEdits]);

  useBeforeUnload(handleBeforeUnload, { capture: true });

  /**
   * 选中范围变化后刷新合并采购预览，展示 MOQ、阶梯价和库存差异。
   */
  useEffect(() => {
    let cancelled = false;
    if (!bomOrder?._id || !selectedItemIdsKey) {
      setPurchasePreview(null);
      return undefined;
    }

    previewBomPurchase({
      bomId: bomOrder._id,
      sessionId,
      itemIds: selectedItemIdsKey.split(","),
    }).unwrap()
      .then((response) => {
        if (!cancelled) setPurchasePreview(response?.preview || null);
      })
      .catch(() => {
        if (!cancelled) setPurchasePreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [bomOrder?._id, bomOrder?.updatedAt, previewBomPurchase, selectedItemIdsKey, sessionId]);

  /**
   * 页面卸载时清理尚未执行的自动保存计时器。
   */
  useEffect(() => () => {
    if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
  }, []);

  /**
   * 用后端返回的 BOM 任务刷新当前表格和默认选中项。
   * @param {Object|null} nextBomOrder BOM 任务
   */
  const refreshBomOrder = useCallback((nextBomOrder) => {
    const nextBomId = String(nextBomOrder?._id || "");
    if (nextBomId && nextBomId !== String(bomId || "")) {
      purchaseSetsBomIdRef.current = nextBomId;
      setPurchaseSets(1);
      setPurchaseSetsPreviewDialog(null);
    }
    setBomOrder(nextBomOrder || null);
    setSelectedItemIds(
      new Set((nextBomOrder?.items || []).filter((item) => item.selected).map((item) => item._id))
    );
    if (nextBomOrder?._id && String(nextBomOrder._id) !== String(bomId || "")) {
      navigate(`/my-parts/${nextBomOrder._id}`, { replace: true });
    }
  }, [bomId, navigate]);

  /**
   * 根据 URL 中的 BOM ID 恢复当前会话可访问的 BOM。
   */
  useEffect(() => {
    if (restoredBomData?.bomOrder) refreshBomOrder(restoredBomData.bomOrder);
  }, [refreshBomOrder, restoredBomData]);

  /**
   * URL 切换到另一份 BOM 时立即重置采购套数，避免等待详情响应期间保留旧状态。
   */
  useEffect(() => {
    purchaseSetsBomIdRef.current = String(bomId || "");
    setPurchaseSets(1);
    setPurchaseSetsPreviewDialog(null);
  }, [bomId]);

  /**
   * 显示 BOM 恢复失败原因。
   */
  useEffect(() => {
    if (restoreBomError) {
      toast.error(restoreBomError?.data?.message || "Failed to restore BOM");
    }
  }, [restoreBomError]);

  /**
   * 记录 Univer API，供保存表格编辑时读取当前工作簿。
   * @param {Object|null} univerAPI Univer facade API
   */
  const handleUniverReady = useCallback((univerAPI) => {
    univerApiRef.current = univerAPI;
  }, []);

  /**
   * 上传 BOM 文件并进入在线表格。
   * @param {File} file BOM 文件
   */
  const handleUploadFile = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("sessionId", sessionId);

    try {
      const response = await uploadBom(formData).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success("BOM online spreadsheet generated");
    } catch (error) {
      handleBomRequestError(error, "Failed to upload BOM");
    }
  };

  /**
   * 处理文件输入框变化。
   * @param {Event} event 输入事件
   */
  const handleFileChange = (event) => {
    handleUploadFile(event.target.files?.[0]);
    event.target.value = "";
  };

  /**
   * 处理拖放到上传区域的 BOM 文件。
   * @param {DragEvent} event 拖放事件
   */
  const handleFileDrop = (event) => {
    event.preventDefault();
    if (isCreatingInitialBom) return;
    handleUploadFile(event.dataTransfer.files?.[0]);
  };

  /**
   * 触发选择 BOM 文件。
   */
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  /**
   * 创建带一条可编辑物料行的空白 BOM 并进入工作台。
   */
  const handleCreateBlankBom = async () => {
    try {
      const response = await createBlankBom({ sessionId }).unwrap();
      setActiveStatuses(new Set(MATCH_STATUS));
      setOnlySelected(false);
      refreshBomOrder(response?.bomOrder || null);
      toast.success("Blank BOM created");
    } catch (error) {
      handleBomRequestError(error, "Failed to create blank BOM");
    }
  };

  /**
   * 提交逐行录入的料号和数量，并进入匹配后的 BOM 工作台。
   * @param {Event} event 表单提交事件
   */
  const handleCreateManualBom = async (event) => {
    event.preventDefault();
    if (!manualInput.trim()) {
      toast.info("Enter at least one part number");
      return;
    }

    try {
      const response = await createManualBom({ sessionId, input: manualInput }).unwrap();
      setActiveStatuses(new Set(MATCH_STATUS));
      setOnlySelected(false);
      setManualInput("");
      setImportMode("upload");
      refreshBomOrder(response?.bomOrder || null);
      toast.success("Manual BOM generated");
    } catch (error) {
      handleBomRequestError(error, "Failed to generate manual BOM");
    }
  };

  /**
   * 提交从 Excel/WPS 粘贴的二维表格，并进入匹配后的 BOM 工作台。
   * @param {Event} event 表单提交事件
   */
  const handleCreateClipboardBom = async (event) => {
    event.preventDefault();
    if (!clipboardInput.trim()) {
      toast.info("Paste a spreadsheet range with headers first");
      return;
    }

    try {
      const response = await createClipboardBom({ sessionId, input: clipboardInput }).unwrap();
      setActiveStatuses(new Set(MATCH_STATUS));
      setOnlySelected(false);
      setClipboardInput("");
      setImportMode("upload");
      refreshBomOrder(response?.bomOrder || null);
      toast.success("Clipboard BOM generated");
    } catch (error) {
      handleBomRequestError(error, "Failed to generate clipboard BOM");
    }
  };

  /**
   * 切换匹配状态筛选。
   * @param {string} status 匹配状态
   */
  const toggleStatus = (status) => {
    const nextStatuses = new Set(activeStatuses);
    if (nextStatuses.has(status)) {
      nextStatuses.delete(status);
    } else {
      nextStatuses.add(status);
    }
    setActiveStatuses(nextStatuses);
  };

  /**
   * 切换全部状态筛选。
   * @param {boolean} checked 是否全选
   */
  const toggleAllStatus = (checked) => {
    setActiveStatuses(new Set(checked ? MATCH_STATUS : []));
  };

  /**
   * 切换当前筛选范围内可加购明细的全选状态。
   * @param {boolean} checked 是否全选
   */
  const toggleVisibleSelection = async (checked) => {
    const selectableItems = filteredItems.filter(canSelectBomItem);
    if (selectableItems.length === 0) return;
    const nextIds = new Set(selectedItemIds);
    selectableItems.forEach((item) => {
      if (checked) nextIds.add(item._id);
      else nextIds.delete(item._id);
    });
    setSelectedItemIds(nextIds);

    const patches = selectableItems.map((item) => ({ itemId: item._id, selected: checked }));
    try {
      const response = await updateBomItems({
        bomId: bomOrder._id,
        sessionId,
        patches,
        expectedUpdatedAt: bomOrder.updatedAt,
      }).unwrap();
      refreshBomOrder(response?.bomOrder || bomOrder);
    } catch (error) {
      if (isBomWriteConflict(error)) {
        setOverwriteConflictDialog({ operation: "selection", patches });
        return;
      }
      toast.error(error?.data?.message || "Failed to save bulk selection");
    }
  };

  /**
   * 保存当前表格数量后，打开 Apply Sets 逐行覆盖预览。
   */
  const openPurchaseSetsPreview = async () => {
    if (!bomOrder?._id) return;
    if (isBomRouteTransitioning) {
      toast.info("The selected BOM is still loading. Please try again shortly");
      return;
    }
    const saved = await flushPendingAutoSave();
    if (!saved) return;

    const livePatches = extractBomSheetRows(univerApiRef.current, filteredItems, visibleColumns);
    const safePurchaseSets = resolvePurchaseSetsForBom(
      purchaseSets,
      purchaseSetsBomIdRef.current,
      String(bomOrder._id),
    );
    setPurchaseSetsPreviewDialog(buildPurchaseSetsPreview(
      bomOrder.items || [],
      safePurchaseSets,
      livePatches,
    ));
  };

  /**
   * 更新采购套数并记录其所属 BOM，供切换任务后的安全校验使用。
   * @param {Event} event 套数输入事件
   */
  const handlePurchaseSetsChange = (event) => {
    purchaseSetsBomIdRef.current = String(bomOrder?._id || bomId || "");
    setPurchaseSets(event.target.value);
  };

  /**
   * 确认预览后批量覆盖所有行的订购量，并同步保存计算依据。
   */
  const confirmApplyPurchaseSets = async () => {
    if (!bomOrder?._id || !purchaseSetsPreviewDialog) return;

    const patches = purchaseSetsPreviewDialog.rows.map((row) => ({
      itemId: row.itemId,
      quantity: row.nextOrderQty,
      normalized: {
        unitQty: row.unitQty,
        productionSurplusQty: row.spareQty,
        orderQty: row.nextOrderQty,
      },
    }));
    try {
      const response = await applyBomPurchaseSets({
        bomId: bomOrder._id,
        sessionId,
        patches,
        expectedUpdatedAt: bomOrder.updatedAt,
      }).unwrap();
      refreshBomOrder(response?.bomOrder || bomOrder);
      setPurchaseSets(purchaseSetsPreviewDialog.sets);
      setPurchaseSetsPreviewDialog(null);
      toast.success(`Order Qty updated for ${purchaseSetsPreviewDialog.rows.length} row(s)`);
    } catch (error) {
      if (isBomWriteConflict(error)) {
        setOverwriteConflictDialog({
          operation: "purchaseSets",
          patches,
          purchaseSets: purchaseSetsPreviewDialog.sets,
          rowCount: purchaseSetsPreviewDialog.rows.length,
        });
        return;
      }
      toast.error(error?.data?.message || "Failed to apply purchase sets");
    }
  };

  /**
   * 保存 Univer 表格中的可编辑 BOM 字段。
   * @param {Object} options 保存选项
   * @param {boolean} options.silent 是否静默保存
   * @param {string[]} options.itemIds 仅保存指定行
   * @param {Object[]} options.patchesOverride 已在编辑事件中捕获的补丁
   * @returns {Promise<boolean>} 是否保存成功
   */
  const saveSheetEdits = async ({ silent = false, itemIds = [], patchesOverride = null } = {}) => {
    if (!bomOrder?._id || !univerApiRef.current) return true;

    const itemIdSet = new Set(itemIds);
    const patches = (patchesOverride || extractBomSheetRows(univerApiRef.current, filteredItems, visibleColumns))
      .filter((patch) => itemIdSet.size === 0 || itemIdSet.has(String(patch.itemId)));
    if (patches.length === 0) return true;
    setRowOperationStates((states) => ({
      ...states,
      ...Object.fromEntries(patches.map((patch) => [patch.itemId, states[patch.itemId] || "saving"])),
    }));

    try {
      const response = await updateBomItems({
        bomId: bomOrder._id,
        sessionId,
        patches,
        expectedUpdatedAt: bomOrder.updatedAt,
      }).unwrap();
      refreshBomOrder(response?.bomOrder || bomOrder);
      const rematchedIds = new Set(response?.rematchedItemIds || []);
      setRowOperationStates((states) => ({
        ...states,
        ...Object.fromEntries(patches.map((patch) => [
          patch.itemId,
          pendingAutoSaveIdsRef.current.has(String(patch.itemId))
            ? states[patch.itemId] || "saving"
            : (rematchedIds.has(String(patch.itemId)) ? "rematched" : "saved"),
        ])),
      }));
      if (!silent) toast.success("Spreadsheet changes saved");
      return true;
    } catch (error) {
      if (isBomWriteConflict(error)) {
        setOverwriteConflictDialog({ operation: "spreadsheet", patches, silent });
        setRowOperationStates((states) => ({
          ...states,
          ...Object.fromEntries(patches.map((patch) => [patch.itemId, "conflict"])),
        }));
        return false;
      }
      setRowOperationStates((states) => ({
        ...states,
        ...Object.fromEntries(patches.map((patch) => [patch.itemId, "error"])),
      }));
      toast.error(error?.data?.message || "Failed to save spreadsheet changes");
      return false;
    }
  };

  /**
   * 用户确认后，将冲突操作重新应用到服务端最新 BOM 上。
   */
  const confirmOverwriteConflict = async () => {
    if (!bomOrder?._id || !overwriteConflictDialog) return;
    setIsResolvingOverwriteConflict(true);

    try {
      const response = await updateBomItems({
        bomId: bomOrder._id,
        sessionId,
        patches: overwriteConflictDialog.patches,
        expectedUpdatedAt: bomOrder.updatedAt,
        forceOverwrite: true,
      }).unwrap();
      refreshBomOrder(response?.bomOrder || bomOrder);

      if (overwriteConflictDialog.operation === "spreadsheet") {
        const rematchedIds = new Set(response?.rematchedItemIds || []);
        setRowOperationStates((states) => ({
          ...states,
          ...Object.fromEntries(overwriteConflictDialog.patches.map((patch) => [
            patch.itemId,
            rematchedIds.has(String(patch.itemId)) ? "rematched" : "saved",
          ])),
        }));
      }
      if (overwriteConflictDialog.operation === "purchaseSets") {
        setPurchaseSets(overwriteConflictDialog.purchaseSets);
        setPurchaseSetsPreviewDialog(null);
      }

      setOverwriteConflictDialog(null);
      toast.success("已使用当前修改覆盖冲突字段");
    } catch (error) {
      handleBomRequestError(error, "覆盖 BOM 修改失败，请重试");
    } finally {
      setIsResolvingOverwriteConflict(false);
    }
  };

  /**
   * 放弃本地冲突修改并重新加载服务端最新 BOM。
   */
  const cancelOverwriteAndReload = async () => {
    setIsResolvingOverwriteConflict(true);
    try {
      const result = await refetchBomOrder().unwrap();
      if (result?.bomOrder) refreshBomOrder(result.bomOrder);
      setRowOperationStates({});
      setPurchaseSetsPreviewDialog(null);
      setOverwriteConflictDialog(null);
      toast.info("已加载其他页面保存的最新 BOM");
    } catch (error) {
      handleBomRequestError(error, "加载最新 BOM 失败，请重试");
    } finally {
      setIsResolvingOverwriteConflict(false);
    }
  };

  /**
   * 串行刷新待自动保存行，避免连续编辑产生并发响应覆盖新结果。
   * @returns {Promise<boolean>} 所有当前待保存修改是否成功
   */
  const flushPendingAutoSave = async () => {
    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (autoSaveInFlightRef.current) {
      const currentSaved = await autoSavePromiseRef.current;
      if (!currentSaved) return false;
      if (pendingAutoSaveIdsRef.current.size > 0) return flushPendingAutoSave();
      return true;
    }
    if (pendingAutoSaveIdsRef.current.size === 0) return true;
    const itemIds = Array.from(pendingAutoSaveIdsRef.current);
    const patches = itemIds
      .map((itemId) => pendingAutoSavePatchesRef.current.get(itemId))
      .filter(Boolean);
    pendingAutoSaveIdsRef.current.clear();
    itemIds.forEach((itemId) => pendingAutoSavePatchesRef.current.delete(itemId));
    autoSaveInFlightRef.current = true;
    const savePromise = saveSheetEdits({ silent: true, itemIds, patchesOverride: patches });
    autoSavePromiseRef.current = savePromise;
    try {
      return await savePromise;
    } finally {
      autoSaveInFlightRef.current = false;
      if (pendingAutoSaveIdsRef.current.size > 0) {
        autoSaveTimerRef.current = window.setTimeout(flushPendingAutoSave, 100);
      }
    }
  };

  /**
   * 确保当前 BOM 的待保存行已提交，保存失败时阻止后续离开操作。
   * @returns {Promise<boolean>} 是否可以安全继续离开操作
   */
  const ensureBomChangesSaved = async () => {
    if (hasRowSaveErrors) {
      toast.error(BOM_UNSAVED_CHANGES_MESSAGE);
      return false;
    }

    const saved = await flushPendingAutoSave();
    const stillSaving = pendingAutoSaveIdsRef.current.size > 0 || autoSaveInFlightRef.current;
    if (!saved || stillSaving) {
      toast.error(BOM_UNSAVED_CHANGES_MESSAGE);
      return false;
    }
    return true;
  };

  saveProtectionRef.current = ensureBomChangesSaved;

  /**
   * 判断一次站内导航是否需要被 BOM 保存保护拦截。
   * @returns {boolean} 是否阻止本次导航
   */
  const shouldBlockNavigation = useCallback(() => {
    if (allowNextNavigationRef.current) {
      allowNextNavigationRef.current = false;
      return false;
    }
    return hasUnsafePurchaseEdits;
  }, [hasUnsafePurchaseEdits]);

  const navigationBlocker = useBlocker(shouldBlockNavigation);
  navigationBlockerRef.current = navigationBlocker;

  /**
   * 对顶部导航、链接和浏览器前进后退统一执行保存保护。
   */
  useEffect(() => {
    if (navigationBlocker.state !== "blocked") return undefined;

    let cancelled = false;
    saveProtectionRef.current?.().then((canLeave) => {
      if (cancelled || navigationBlockerRef.current?.state !== "blocked") return;
      if (canLeave) navigationBlockerRef.current.proceed();
      else navigationBlockerRef.current.reset();
    });

    return () => {
      cancelled = true;
    };
  }, [navigationBlocker.state]);

  /**
   * 保存当前 BOM 后执行站内跳转，并避免同一次跳转被重复拦截。
   * @param {string} targetPath 目标站内路径
   * @param {Object} options React Router 导航选项
   * @param {Function|null} beforeNavigate 跳转前的本地状态处理
   * @returns {Promise<boolean>} 是否已发起导航
   */
  const navigateAfterSaveProtection = async (targetPath, options = {}, beforeNavigate = null) => {
    const canLeave = await ensureBomChangesSaved();
    if (!canLeave) return false;

    allowNextNavigationRef.current = true;
    beforeNavigate?.();
    navigate(targetPath, options);
    return true;
  };

  /**
   * 保存当前 BOM 后打开新文件选择框。
   */
  const openNewBomPickerAfterSaveProtection = async () => {
    const canLeave = await ensureBomChangesSaved();
    if (canLeave) openFilePicker();
  };

  /**
   * 响应 Univer 单元格变化，标记受影响行并防抖执行行级保存和重新匹配。
   * @param {Object} params Univer 值变化事件
   */
  const handleSheetValueChanged = (params = {}) => {
    const matchingFields = new Set([
      "originalDescription",
      "originalManufacturer",
      "originalMpn",
      "originalSupplierPartNo",
      "packageSize",
    ]);
    const nextStates = {};
    (params.effectedRanges || []).forEach((effectedRange) => {
      const range = effectedRange?.getRange?.();
      if (!range) return;
      const startRow = Math.max(1, Number(range.startRow || 0));
      const endRow = Math.min(filteredItems.length, Number(range.endRow ?? range.startRow ?? 0));
      const startColumn = Math.max(0, Number(range.startColumn || 0));
      const endColumn = Math.min(visibleColumns.length - 1, Number(range.endColumn ?? range.startColumn ?? 0));
      const affectedColumns = visibleColumns.slice(startColumn, endColumn + 1);
      if (!affectedColumns.some((column) => column.editable)) return;
      const needsRematch = affectedColumns
        .some((column) => matchingFields.has(column.field));

      for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
        const itemId = filteredItems[rowIndex - 1]?._id;
        if (!itemId) continue;
        pendingAutoSaveIdsRef.current.add(String(itemId));
        nextStates[itemId] = needsRematch ? "matching" : "saving";
      }
    });
    if (Object.keys(nextStates).length === 0) return;
    const changedIds = new Set(Object.keys(nextStates));
    extractBomSheetRows(univerApiRef.current, filteredItems, visibleColumns)
      .filter((patch) => changedIds.has(String(patch.itemId)))
      .forEach((patch) => pendingAutoSavePatchesRef.current.set(String(patch.itemId), patch));
    setPurchasePreview(null);
    setRowOperationStates((states) => ({ ...states, ...nextStates }));
    if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = window.setTimeout(flushPendingAutoSave, 800);
  };

  /**
   * 打开自定义列面板并创建一份待应用配置。
   */
  const openColumnSettings = () => {
    setHeaderMappingDraft(null);
    setColumnDraft({
      order: [...effectiveColumnPreferences.order],
      hiddenKeys: [...effectiveColumnPreferences.hiddenKeys],
    });
  };

  /**
   * 打开表头映射纠错面板，载入当前解析结果。
   */
  const openHeaderMapping = () => {
    if (!Array.isArray(bomOrder?.sourceRows) || bomOrder.sourceRows.length === 0) {
      toast.info("This BOM has no saved source rows for header mapping");
      return;
    }
    setColumnDraft(null);
    setHeaderMappingDraft({
      headerRowIndex: Number(bomOrder.headerRowIndex || 0),
      fieldMap: { ...(bomOrder.fieldMap || {}) },
    });
  };

  /**
   * 更新单个标准字段对应的原始列。
   * @param {string} fieldName 标准字段名
   * @param {string} columnIndex 原始列索引
   */
  const updateHeaderMappingField = (fieldName, columnIndex) => {
    setHeaderMappingDraft((draft) => {
      if (!draft) return draft;
      const fieldMap = { ...draft.fieldMap };
      if (columnIndex === "") delete fieldMap[fieldName];
      else fieldMap[fieldName] = Number(columnIndex);
      return { ...draft, fieldMap };
    });
  };

  /**
   * 提交表头行和字段映射，重新解析并匹配当前 BOM。
   */
  const applyHeaderMapping = async () => {
    if (!bomOrder?._id || !headerMappingDraft) return;
    try {
      const response = await reparseBom({
        bomId: bomOrder._id,
        sessionId,
        headerRowIndex: headerMappingDraft.headerRowIndex,
        fieldMap: headerMappingDraft.fieldMap,
      }).unwrap();
      setHeaderMappingDraft(null);
      setActiveStatuses(new Set(MATCH_STATUS));
      setOnlySelected(false);
      refreshBomOrder(response?.bomOrder || null);
      toast.success("Headers remapped and BOM rematched");
    } catch (error) {
      handleBomRequestError(error, "Failed to reparse BOM headers");
    }
  };

  /**
   * 切换草稿中指定列的显示状态，至少保留一列。
   * @param {string} columnKey 列标识
   */
  const toggleDraftColumn = (columnKey) => {
    setColumnDraft((draft) => {
      if (!draft) return draft;
      const hiddenKeys = new Set(draft.hiddenKeys);
      if (hiddenKeys.has(columnKey)) {
        hiddenKeys.delete(columnKey);
      } else if (draft.order.length - hiddenKeys.size > 1) {
        hiddenKeys.add(columnKey);
      } else {
        toast.info("At least one column must remain visible");
      }
      return { ...draft, hiddenKeys: Array.from(hiddenKeys) };
    });
  };

  /**
   * 将草稿中的列移动一个位置，供键盘和按钮操作使用。
   * @param {string} columnKey 列标识
   * @param {-1|1} direction 移动方向
   */
  const moveDraftColumn = (columnKey, direction) => {
    setColumnDraft((draft) => {
      if (!draft) return draft;
      const order = [...draft.order];
      const currentIndex = order.indexOf(columnKey);
      const targetIndex = currentIndex + direction;
      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= order.length) return draft;
      [order[currentIndex], order[targetIndex]] = [order[targetIndex], order[currentIndex]];
      return { ...draft, order };
    });
  };

  /**
   * 将拖动列放到目标列之前。
   * @param {string} targetKey 目标列标识
   */
  const dropDraftColumn = (targetKey) => {
    if (!draggedColumnKey || draggedColumnKey === targetKey) return;
    setColumnDraft((draft) => {
      if (!draft) return draft;
      const order = draft.order.filter((key) => key !== draggedColumnKey);
      const targetIndex = order.indexOf(targetKey);
      order.splice(targetIndex < 0 ? order.length : targetIndex, 0, draggedColumnKey);
      return { ...draft, order };
    });
    setDraggedColumnKey("");
  };

  /**
   * 保存当前编辑后应用并持久化自定义列配置。
   */
  const applyColumnSettings = async () => {
    if (!columnDraft) return;
    const saved = await saveSheetEdits({ silent: true });
    if (!saved) return;
    saveBomColumnPreferences(columnDraft);
    setColumnPreferences(columnDraft);
    setColumnDraft(null);
    toast.success("Column settings saved");
  };

  /**
   * 将列设置草稿恢复为项目默认值。
   */
  const resetColumnSettings = () => {
    setColumnDraft({
      order: allColumns.map((column) => column.key),
      hiddenKeys: allColumns.filter((column) => column.defaultHidden).map((column) => column.key),
    });
  };

  /**
   * 在当前 BOM 云表末尾新增一条可编辑物料行。
   */
  const handleCreateItem = async () => {
    if (!bomOrder?._id) return;

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await createBomItem({
        bomId: bomOrder._id,
        sessionId,
        normalized: { unitQty: 1, productionSurplusQty: 0 },
      }).unwrap();
      setActiveStatuses((statuses) => new Set([...statuses, "invalid"]));
      setOnlySelected(false);
      refreshBomOrder(response?.bomOrder || null);
      toast.success("Item added. Enter a part number or C-number");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add item");
    }
  };

  /**
   * 删除 Univer 当前选区覆盖的 BOM 物料行。
   */
  const handleDeleteSelectedRows = async () => {
    if (!bomOrder?._id || !univerApiRef.current) return;

    const itemIds = getSelectedBomItemIds(univerApiRef.current, filteredItems);
    if (itemIds.length === 0) {
      toast.info("Select the item rows to delete in the spreadsheet first");
      return;
    }
    if (!window.confirm(`Delete the ${itemIds.length} selected item row(s)?`)) return;

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await deleteBomItems({
        bomId: bomOrder._id,
        sessionId,
        itemIds,
      }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success(`${response?.deletedCount || itemIds.length} item row(s) deleted`);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to delete items");
    }
  };

  /**
   * 重新匹配当前 BOM 任务。
   */
  const handleRematch = async () => {
    if (!bomOrder?._id) return;

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await rematchBom({ bomId: bomOrder._id, sessionId }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success("BOM rematched");
    } catch (error) {
      handleBomRequestError(error, "Failed to rematch BOM");
    }
  };

  /**
   * 使用 AI 为当前选区中的无法匹配 BOM 行生成待确认候选。
   */
  const handleAiRematch = async () => {
    if (!bomOrder?._id || !univerApiRef.current || Number(summary.unmatchedRows || 0) === 0) return;

    const unmatchedItemIds = new Set(
      (bomOrder.items || [])
        .filter((item) => item.matchStatus === "unmatched")
        .map((item) => String(item._id)),
    );
    const itemIds = getSelectedBomItemIds(univerApiRef.current, filteredItems)
      .filter((itemId) => unmatchedItemIds.has(String(itemId)));

    if (itemIds.length === 0) {
      toast.info("Select the unmatched item rows to AI match in the spreadsheet first");
      return;
    }
    if (itemIds.length > MAX_AI_REMATCH_ROWS) {
      toast.info(`Select no more than ${MAX_AI_REMATCH_ROWS} unmatched rows at a time`);
      return;
    }

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await aiRematchBom({ bomId: bomOrder._id, sessionId, itemIds }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      const pendingCount = Number(response?.ai?.pendingConfirmationRows || 0);
      toast.success(
        pendingCount > 0
          ? `AI found ${pendingCount} local candidate(s). Please review them manually`
          : "AI analysis completed, but no candidates were found in the local product catalog",
      );
    } catch (error) {
      handleBomRequestError(error, "AI matching failed");
    }
  };

  /**
   * 接受或拒绝单条 AI 补配候选。
   * @param {string} itemId BOM 明细 ID
   * @param {"accept"|"reject"} decision 审核决定
   */
  const handleAiReview = async (itemId, decision) => {
    if (!bomOrder?._id || !itemId) return;

    try {
      const response = await reviewBomAiSuggestion({
        bomId: bomOrder._id,
        itemId,
        sessionId,
        decision,
      }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success(decision === "accept" ? "AI match confirmed" : "AI match rejected");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to review AI match");
    }
  };

  /**
   * 打开当前云表选中行的候选确认与替换面板。
   */
  const handleOpenCandidateReview = () => {
    if (!univerApiRef.current) return;
    const itemIds = getSelectedBomItemIds(univerApiRef.current, filteredItems);
    if (itemIds.length !== 1) {
      toast.info("Select exactly one item row to review or change its matched part");
      return;
    }
    setCandidateReviewItemId(itemIds[0]);
    setCandidateKeyword("");
    setSearchedCandidates(null);
  };

  /**
   * 搜索当前 BOM 行的可替换候选。
   * @param {Event} event 表单提交事件
   */
  const handleCandidateSearch = async (event) => {
    event.preventDefault();
    if (!bomOrder?._id || !candidateReviewItem?._id) return;
    try {
      const response = await searchBomItemCandidates({
        bomId: bomOrder._id,
        itemId: candidateReviewItem._id,
        sessionId,
        keyword: candidateKeyword,
      }).unwrap();
      setSearchedCandidates(response?.candidates || []);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to search replacement parts");
    }
  };

  /**
   * 确认普通匹配候选或将已匹配商品替换为指定候选。
   * @param {string} productId 商品 ID
   */
  const handleConfirmCandidate = async (productId) => {
    if (!bomOrder?._id || !candidateReviewItem?._id || !productId) return;
    try {
      const response = await updateBomItem({
        bomId: bomOrder._id,
        itemId: candidateReviewItem._id,
        sessionId,
        payload: { selectedProduct: productId },
      }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      setCandidateReviewItemId("");
      setCandidateKeyword("");
      setSearchedCandidates(null);
      toast.success("Matched part confirmed");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to confirm matched part");
    }
  };

  /**
   * 保存在线表格编辑结果后，导出当前 BOM 的 Excel 报价文件。
   */
  const handleExportBom = async () => {
    if (!bomOrder?._id || isExporting) return;
    setIsExporting(true);
    try {
      const exportSelectedIds = new Set(
        (bomOrder.items || []).filter((item) => item.selected).map((item) => item._id),
      );
      const livePatches = extractBomSheetRows(univerApiRef.current, filteredItems, visibleColumns);
      livePatches.forEach((patch) => {
        if (patch.selected) exportSelectedIds.add(patch.itemId);
        else exportSelectedIds.delete(patch.itemId);
      });
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;

      const exportScope = exportSelectedIds.size > 0 ? "selected" : "all";
      const query = new URLSearchParams({ sessionId, scope: exportScope });
      const response = await fetch(
        `${BASE_URL || ""}${URL}/bom/${bomOrder._id}/export?${query.toString()}`,
        { credentials: "include" },
      );
      if (!response.ok) {
        throw await createBomResponseError(response, "Failed to export BOM");
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = objectUrl;
      downloadLink.download = `Creatyea_BOM_${bomOrder._id}.xlsx`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.URL.revokeObjectURL(objectUrl);
      toast.success(exportScope === "selected" ? "Selected BOM items exported" : "BOM exported");
    } catch (error) {
      handleBomRequestError(error, "Failed to export BOM");
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * 将已选 BOM 明细批量加入现有购物车。
   */
  const addSelectedToCart = async () => {
    if (!bomOrder?._id || selectedItemIds.size === 0) return;

    try {
      if (hasRowSaveErrors) {
        toast.error("Please save failed BOM rows before adding items to cart");
        return;
      }
      const changesSaved = await flushPendingAutoSave();
      if (!changesSaved || pendingAutoSaveIdsRef.current.size > 0 || autoSaveInFlightRef.current) {
        toast.error("BOM changes are not saved yet. Please retry after saving completes");
        return;
      }
      const response = await buildBomCartItems({
        bomId: bomOrder._id,
        sessionId,
        itemIds: Array.from(selectedItemIds),
      }).unwrap();

      (response?.cartItems || []).forEach((cartItem) => dispatch(setCartItem(cartItem)));
      toast.success(`${response?.cartItems?.length || 0} BOM item(s) added to cart`);
      allowNextNavigationRef.current = true;
      navigate("/cart");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add items to cart");
    }
  };

  /**
   * 保存当前表格的待处理修改后切换到指定历史 BOM。
   * @param {string} targetBomId 目标 BOM ID
   */
  const handleOpenHistoryBom = async (targetBomId) => {
    if (String(targetBomId) === String(bomOrder?._id || "")) return;
    if (!bomOrder) {
      navigate(`/my-parts/${targetBomId}`);
      return;
    }
    await navigateAfterSaveProtection(
      `/my-parts/${targetBomId}`,
      {},
      () => setHistoryOpen(false),
    );
  };

  const visibleSelectableItems = filteredItems.filter(canSelectBomItem);
  const allVisibleSelected = visibleSelectableItems.length > 0
    && visibleSelectableItems.every((item) => selectedItemIds.has(item._id));

  return (
    <main className={`bom-order-page ${bomOrder ? "is-sheet-mode" : ""}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx,.csv"
        className="bom-hidden-file"
        onChange={handleFileChange}
        disabled={isCreatingInitialBom}
      />

      <Modal
        show={Boolean(rateLimitMessage)}
        onHide={() => setRateLimitMessage("")}
        centered
        aria-labelledby="bom-rate-limit-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="bom-rate-limit-title">操作过于频繁</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="bom-rate-limit-message">{rateLimitMessage}</p>
          <p className="bom-rate-limit-help">请稍后再试，无需重复提交当前操作。</p>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="bom-primary-button"
            onClick={() => setRateLimitMessage("")}
          >
            我知道了
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(overwriteConflictDialog)}
        onHide={cancelOverwriteAndReload}
        centered
        backdrop="static"
        keyboard={!isResolvingOverwriteConflict}
        aria-labelledby="bom-overwrite-conflict-title"
      >
        <Modal.Header>
          <Modal.Title id="bom-overwrite-conflict-title">检测到其他页面的修改</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>此 BOM 已在其他标签页或设备中更新。继续覆盖会把当前修改应用到服务端最新数据的相同字段。</p>
          <p>你可以取消覆盖并加载最新数据，或确认使用当前修改覆盖冲突字段。</p>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="bom-secondary-button"
            disabled={isResolvingOverwriteConflict}
            onClick={cancelOverwriteAndReload}
          >
            {isResolvingOverwriteConflict ? "处理中..." : "取消并加载最新数据"}
          </button>
          <button
            type="button"
            className="bom-primary-button"
            disabled={isResolvingOverwriteConflict}
            onClick={confirmOverwriteConflict}
          >
            {isResolvingOverwriteConflict ? "处理中..." : "仍要覆盖"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(purchaseSetsPreviewDialog)}
        onHide={() => {
          if (!isApplyingPurchaseSets) setPurchaseSetsPreviewDialog(null);
        }}
        centered
        scrollable
        size="xl"
        backdrop={isApplyingPurchaseSets ? "static" : true}
        keyboard={!isApplyingPurchaseSets}
        aria-labelledby="bom-purchase-sets-preview-title"
      >
        <Modal.Header closeButton={!isApplyingPurchaseSets}>
          <Modal.Title id="bom-purchase-sets-preview-title">Apply Sets Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="bom-sets-preview-formula">
            <strong>Formula:</strong> Order Qty = Qty per Set × {purchaseSetsPreviewDialog?.sets || 1} set(s) + Spare Qty
          </p>
          <div className="bom-sets-preview-warning" role="alert">
            Confirming will overwrite the existing or imported Order Qty for all {purchaseSetsPreviewDialog?.rows.length || 0} row(s).
            {` ${purchaseSetsPreviewDialog?.changedCount || 0} row(s) will change.`}
          </div>
          <div className="bom-sets-preview-table-wrap">
            <table className="bom-sets-preview-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Part Number</th>
                  <th>Current Order Qty</th>
                  <th>Qty per Set</th>
                  <th>Spare Qty</th>
                  <th>New Order Qty</th>
                </tr>
              </thead>
              <tbody>
                {(purchaseSetsPreviewDialog?.rows || []).map((row) => (
                  <tr key={row.itemId} className={row.changed ? "is-changed" : ""}>
                    <td>{row.lineNo}</td>
                    <td>{row.partNumber}</td>
                    <td>{row.currentOrderQty}</td>
                    <td>{row.unitQty}</td>
                    <td>{row.spareQty}</td>
                    <td><strong>{row.nextOrderQty}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="bom-secondary-button"
            disabled={isApplyingPurchaseSets}
            onClick={() => setPurchaseSetsPreviewDialog(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bom-primary-button"
            disabled={isApplyingPurchaseSets}
            onClick={confirmApplyPurchaseSets}
          >
            {isApplyingPurchaseSets ? "Applying..." : "Confirm & Overwrite Order Qty"}
          </button>
        </Modal.Footer>
      </Modal>

      {!bomOrder ? (
        <div className="bom-landing-shell">
          <header className="bom-upload-header">
            <img className="bom-upload-icon" src="/img/my-parts.svg" alt="" />
            <div>
              <span>CREATYEA</span>
              <h1>My Parts</h1>
            </div>
          </header>

          <div className="bom-feature-grid" aria-label="My Parts features">
            <div>
              <FiCrosshair aria-hidden="true" />
              <span><strong>Quick match within 1 minute</strong><small>Primary matching key: CTYEA Part Number</small></span>
            </div>
            <div>
              <FiClipboard aria-hidden="true" />
              <span><strong>Stock Availability &amp; Pricing</strong><small>Quick view on real-time stock, available quantities, unit prices with matching prioritized by the manufacturer part number.</small></span>
            </div>
            <div>
              <FiEdit3 aria-hidden="true" />
              <span><strong>Spreadsheet-Style Editing</strong><small>Web-Based Editing for Generated Entries</small></span>
            </div>
            <div>
              <FiShoppingCart aria-hidden="true" />
              <span><strong>Add to Cart Upon Confirmation</strong><small>Work with the current shopping cart pipeline</small></span>
            </div>
          </div>

          <section className="bom-import-panel">
            <div className="bom-import-heading">
              <h2>Import Parts</h2>
              <p>Choose a method to import your BOM data.</p>
            </div>
            <div className="bom-import-tabs" role="tablist" aria-label="Import method">
              {[
                { key: "upload", label: "Upload My Parts", icon: <FiUpload /> },
                { key: "clipboard", label: "Paste from Spreadsheet", icon: <FiClipboard /> },
                { key: "manual", label: "Manual Entry", icon: <FiEdit3 /> },
                { key: "blank", label: "Create a list for My Parts", icon: <FiFileText /> },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  className={importMode === item.key ? "is-active" : ""}
                  aria-selected={importMode === item.key}
                  disabled={isCreatingInitialBom}
                  onClick={() => setImportMode(item.key)}
                >
                  {item.icon}<span>{item.label}</span>
                </button>
              ))}
            </div>

            <form
              className="bom-import-form"
              onSubmit={importMode === "clipboard" ? handleCreateClipboardBom : handleCreateManualBom}
            >
              <div className="bom-import-content">
                <div className="bom-import-editor">
                  {importMode === "upload" && (
                    <div className="bom-import-intro">
                      <FiUpload aria-hidden="true" />
                      <h3>Upload My Parts</h3>
                      <p>Upload an Excel or CSV file to match parts, stock availability, and pricing.</p>
                    </div>
                  )}
                  {importMode === "clipboard" && (
                    <>
                      <label htmlFor="bom-clipboard-input">Paste Spreadsheet with Headers</label>
                      <p>Copy a two-dimensional range from Excel or WPS. Include part number/C-number and quantity headers.</p>
                      <textarea
                        id="bom-clipboard-input"
                        rows="8"
                        maxLength="200000"
                        value={clipboardInput}
                        onChange={(event) => setClipboardInput(event.target.value)}
                        placeholder={"Manufacturer Part Number\tQuantity\tManufacturer\tPackage / Size\tDesignator\nSTM32F103C8T6\t2\tSTMicroelectronics\tLQFP-48\tU1"}
                        disabled={isCreatingClipboardBom}
                      />
                    </>
                  )}
                  {importMode === "manual" && (
                    <>
                      <label htmlFor="bom-manual-input">Part Number / CTYEA Part Number and Quantity</label>
                      <p>Enter one item per line. Separate the part number and quantity with a tab, comma, semicolon, or space.</p>
                      <textarea
                        id="bom-manual-input"
                        rows="7"
                        maxLength="50000"
                        value={manualInput}
                        onChange={(event) => setManualInput(event.target.value)}
                        placeholder={"C25804, 10\nSTM32F103C8T6, 2\nNE555, 1"}
                        disabled={isCreatingManualBom}
                      />
                    </>
                  )}
                  {importMode === "blank" && (
                    <div className="bom-import-intro">
                      <FiFileText aria-hidden="true" />
                      <h3>Create a list for My Parts</h3>
                      <p>Start with an empty spreadsheet and add or edit your parts online.</p>
                    </div>
                  )}
                </div>

                <aside className="bom-file-alternative">
                  <strong>{importMode === "upload" ? "Upload a file" : "Or upload a file instead"}</strong>
                  <button
                    type="button"
                    className="bom-file-dropzone"
                    onClick={openFilePicker}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={handleFileDrop}
                    disabled={isCreatingInitialBom}
                  >
                    <FiUpload aria-hidden="true" />
                    <span>Drag and drop your file here, or</span>
                    <u>click to upload</u>
                    <small>Supports xls, xlsx, and csv; maximum file size: 10 MB</small>
                  </button>
                  <a className="bom-template-link" href={getBomTemplateUrl()} download>
                    <FiDownload aria-hidden="true" /> Download BOM Template
                  </a>
                </aside>
              </div>

              <div className="bom-import-actions">
                <button type="button" className="bom-secondary-button" onClick={() => setImportMode("upload")} disabled={isCreatingInitialBom}>
                  Cancel
                </button>
                <button
                  type={importMode === "manual" || importMode === "clipboard" ? "submit" : "button"}
                  className="bom-primary-button"
                  onClick={importMode === "upload" ? openFilePicker : importMode === "blank" ? handleCreateBlankBom : undefined}
                  disabled={isCreatingInitialBom}
                >
                  {isRestoringBom || isUploading
                    ? "Uploading..."
                    : isCreatingBlankBom || isCreatingManualBom || isCreatingClipboardBom
                      ? "Matching..."
                      : "Generate BOM"}
                </button>
              </div>
            </form>
          </section>

          <BomHistoryPanel sessionId={sessionId} onOpen={handleOpenHistoryBom} />
        </div>
      ) : (
        <div className={`bom-sheet-workspace ${isUpperAreaCollapsed ? "is-upper-area-collapsed" : ""}`}>
          {isUpperAreaCollapsed && (
            <section className="bom-sheet-compact-header" aria-label="Collapsed BOM controls">
              <strong title={bomOrder.originalFileName || "BOM Online Sheet"}>
                {bomOrder.originalFileName || "BOM Online Sheet"}
              </strong>
              <button
                type="button"
                onClick={() => setIsUpperAreaCollapsed(false)}
                aria-expanded="false"
                aria-label="展开顶部区域"
                title="展开顶部区域"
              >
                <FiChevronDown aria-hidden="true" />
              </button>
            </section>
          )}
          <header className="bom-sheet-topbar">
            <div className="bom-sheet-left-actions">
              <button
                type="button"
                className="bom-icon-button"
                title="Back to Upload"
                onClick={() => navigateAfterSaveProtection(
                  "/my-parts",
                  {},
                  () => setBomOrder(null),
                )}
              >
                □
              </button>
              <button
                type="button"
                className="bom-icon-button"
                title="Upload New BOM"
                onClick={openNewBomPickerAfterSaveProtection}
              >
                +
              </button>
              <button
                type="button"
                className="bom-history-toggle"
                title="My Parts"
                onClick={() => setHistoryOpen(true)}
              >
                My Parts
              </button>
            </div>
            <div className="bom-sheet-title">
              <h1>{bomOrder.originalFileName || "BOM Online Sheet"}</h1>
              <small title={bomOrder._id}>BOM ID: {bomOrder._id}</small>
              {isAuthenticated ? (
                <div className="bom-related-orders" aria-label="Related orders">
                  <span>Orders:</span>
                  {isLoadingRelatedOrders ? (
                    <em>Loading...</em>
                  ) : relatedOrders.length > 0 ? (
                    relatedOrders.map((relatedOrder) => (
                      <Link key={relatedOrder._id} to={`/me/order/${relatedOrder._id}`}>
                        {relatedOrder.orderNo || relatedOrder._id}
                      </Link>
                    ))
                  ) : (
                    <em>No related orders</em>
                  )}
                </div>
              ) : null}
            </div>
            <div className="bom-sheet-stats">
              <div className="bom-match-card">
                <span className="bom-match-ring" style={{ "--match-rate": `${matchRate}%` }} />
                <strong>{matchRate}%</strong>
                <small>Exact Match Rate</small>
              </div>
              <label className="bom-purchase-sets">
                <span>Purchase Sets</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={purchaseSets}
                  onChange={handlePurchaseSetsChange}
                />
                <em>sets</em>
              </label>
            </div>
          </header>

          {historyOpen && (
            <div
              className="bom-history-overlay"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) setHistoryOpen(false);
              }}
            >
              <div className="bom-history-drawer">
                <BomHistoryPanel
                  sessionId={sessionId}
                  currentBomId={bomOrder._id}
                  onOpen={handleOpenHistoryBom}
                  onClose={() => setHistoryOpen(false)}
                />
              </div>
            </div>
          )}

          <section className="bom-sheet-menu">
            <nav>
              <button type="button">Home</button>
              <button type="button">View</button>
              <button type="button" className="is-active">Filter</button>
            </nav>
            <div className="bom-sheet-actions">
              <button type="button" onClick={handleCreateItem} disabled={isCreatingItem || isDeletingItems}>
                {isCreatingItem ? "Adding..." : "Add Item"}
              </button>
              <button type="button" onClick={handleDeleteSelectedRows} disabled={isCreatingItem || isDeletingItems}>
                {isDeletingItems ? "Deleting..." : "Delete Selected Rows"}
              </button>
              <button
                type="button"
                onClick={openPurchaseSetsPreview}
                disabled={isBomRouteTransitioning}
                title="Preview Order Qty = Qty per Set × sets + Spare Qty before overwriting every row"
              >
                Preview Apply Sets
              </button>
              <span className="bom-apply-sets-note">Overwrites Order Qty for every row</span>
              <button type="button" onClick={() => saveSheetEdits()}>Save Changes</button>
              <button type="button" onClick={handleRematch} disabled={isRematching}>
                {isRematching ? "Matching..." : "Rematch"}
              </button>
              <button type="button" onClick={handleOpenCandidateReview}>
                Review / Change Part
              </button>
              <button
                type="button"
                className="bom-ai-button"
                onClick={handleAiRematch}
                disabled={isAiRematching || Number(summary.unmatchedRows || 0) === 0}
                title="Select up to 20 unmatched rows in the spreadsheet; manual confirmation is still required"
              >
                {isAiRematching ? "AI Matching..." : "AI Match Selected Rows"}
              </button>
              <button type="button" onClick={handleExportBom} disabled={isExporting}>
                {isExporting ? "Exporting..." : "Export Quote"}
              </button>
              <button
                type="button"
                onClick={openHeaderMapping}
                disabled={!bomOrder.sourceRows?.length || isReparsing}
                title={bomOrder.sourceRows?.length ? "Correct the header row and field mappings" : "Source rows were not saved for this BOM"}
              >
                {isReparsing ? "Reparsing..." : "Map Headers"}
              </button>
              <button type="button" onClick={openColumnSettings}>Customize Columns</button>
            </div>
          </section>

          {headerMappingDraft && (
            <section className="bom-column-settings bom-header-mapping" aria-label="BOM header mapping">
              <div className="bom-column-settings-heading">
                <div>
                  <strong>Map Source Headers</strong>
                  <span>Select the real header row and map source columns. Applying will rebuild all match results.</span>
                </div>
                <div className="bom-column-settings-actions">
                  <button type="button" onClick={() => setHeaderMappingDraft(null)} disabled={isReparsing}>Cancel</button>
                  <button type="button" className="is-primary" onClick={applyHeaderMapping} disabled={isReparsing}>
                    {isReparsing ? "Applying..." : "Apply & Rematch"}
                  </button>
                </div>
              </div>
              <label className="bom-header-row-field">
                <span>Header Row</span>
                <input
                  type="number"
                  min="1"
                  max={bomOrder.sourceRows?.length}
                  value={headerMappingDraft.headerRowIndex + 1}
                  onChange={(event) => setHeaderMappingDraft((draft) => ({
                    ...draft,
                    headerRowIndex: Math.max(0, Number(event.target.value || 1) - 1),
                  }))}
                />
              </label>
              <div className="bom-header-mapping-grid">
                {BOM_MAPPING_FIELDS.map((field) => (
                  <label key={field.key}>
                    <span>
                      {field.label}
                      {(field.required || field.requiredGroup) && <em>*</em>}
                    </span>
                    <select
                      value={headerMappingDraft.fieldMap[field.key] ?? ""}
                      onChange={(event) => updateHeaderMappingField(field.key, event.target.value)}
                    >
                      <option value="">Not mapped</option>
                      {headerMappingColumns.map((header, columnIndex) => (
                        <option key={`${columnIndex}-${header}`} value={columnIndex}>
                          {columnIndex + 1}. {`${header || `Column ${columnIndex + 1}`}`}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              <p className="bom-header-mapping-help">* Quantity is required; map at least one of Part Number or Supplier Part Number.</p>
            </section>
          )}

          {columnDraft && (
            <section className="bom-column-settings" aria-label="Customize column settings">
              <div className="bom-column-settings-heading">
                <div>
                  <strong>Customize Columns</strong>
                  <span>Select columns to display, then drag or use the arrows to reorder them. Changes are saved in this browser.</span>
                </div>
                <div className="bom-column-settings-actions">
                  <button type="button" onClick={resetColumnSettings}>Restore Defaults</button>
                  <button type="button" onClick={() => setColumnDraft(null)}>Cancel</button>
                  <button type="button" className="is-primary" onClick={applyColumnSettings}>Apply</button>
                </div>
              </div>
              <div className="bom-column-settings-list">
                {columnDraft.order.map((columnKey, columnIndex) => {
                  const column = allColumns.find((item) => item.key === columnKey);
                  if (!column) return null;
                  const visible = !columnDraft.hiddenKeys.includes(columnKey);
                  return (
                    <div
                      key={columnKey}
                      className={`bom-column-setting-item ${draggedColumnKey === columnKey ? "is-dragging" : ""}`}
                      draggable
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", columnKey);
                        setDraggedColumnKey(columnKey);
                      }}
                      onDragEnd={() => setDraggedColumnKey("")}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => dropDraftColumn(columnKey)}
                    >
                      <span className="bom-column-drag-handle" title="Drag to reorder">⋮⋮</span>
                      <label>
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => toggleDraftColumn(columnKey)}
                        />
                        <strong>{column.title}</strong>
                      </label>
                      <small>{column.editable ? "Editable" : "Read-only"}</small>
                      <div className="bom-column-order-buttons">
                        <button
                          type="button"
                          title="Move Up"
                          disabled={columnIndex === 0}
                          onClick={() => moveDraftColumn(columnKey, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          title="Move Down"
                          disabled={columnIndex === columnDraft.order.length - 1}
                          onClick={() => moveDraftColumn(columnKey, 1)}
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="bom-sheet-filterbar">
            <div className="bom-filter-tool">
              <strong>A-Z</strong>
              <span>Global Filter</span>
            </div>
            <div className="bom-filter-tool">
              <strong>⊙</strong>
              <span>Match Result Filter</span>
            </div>
            <label>
              <input
                type="checkbox"
                checked={activeStatuses.size === MATCH_STATUS.length}
                onChange={(event) => toggleAllStatus(event.target.checked)}
              />
              Select All
            </label>
            {MATCH_STATUS.map((status) => (
              <label key={status}>
                <input
                  type="checkbox"
                  checked={activeStatuses.has(status)}
                  onChange={() => toggleStatus(status)}
                />
                {STATUS_LABELS[status]}
              </label>
            ))}
            <button
              type="button"
              className="bom-collapse-upper-button"
              onClick={() => setIsUpperAreaCollapsed(true)}
              aria-expanded="true"
              aria-label="收起顶部区域"
              title="收起顶部区域"
            >
              <FiChevronUp aria-hidden="true" />
            </button>
          </section>

          {Object.keys(rowOperationStates).length > 0 && (
            <section className="bom-row-statusbar" aria-label="Row save and matching status">
              {(bomOrder.items || []).flatMap((item) => {
                const status = rowOperationStates[item._id];
                if (!status) return [];
                const labels = {
                  saving: "Saving...",
                  matching: "Saving & matching...",
                  saved: "Saved",
                  rematched: "Saved & rematched",
                  error: "Save failed",
                };
                return [(
                  <span key={item._id} className={`is-${status}`}>
                    Row {item.lineNo}: {labels[status]}
                  </span>
                )];
              })}
            </section>
          )}

          {aiPendingItems.length > 0 && (
            <section className="bom-ai-review-panel" aria-label="AI matches awaiting confirmation">
              <div className="bom-ai-review-heading">
                <div>
                  <strong>AI Matches Awaiting Confirmation</strong>
                  <span>AI results are suggestions only. Verify the part number, brand, package, and parameters before confirming.</span>
                </div>
                <em>{aiPendingItems.length} item(s)</em>
              </div>
              <div className="bom-ai-review-list">
                {aiPendingItems.map((item) => {
                  const suggestion = item.aiSuggestion || {};
                  const product = item.productSnapshot || {};
                  return (
                    <article key={item._id} className="bom-ai-review-item">
                      <div className="bom-ai-review-copy">
                        <span>Row {item.lineNo} · AI confidence {item.confidence || 0}%</span>
                        <strong>{product.Manufacturer_Part_Number || suggestion.suggestedMpn || "-"}</strong>
                        <small>
                          {product.Manufacturer || suggestion.suggestedManufacturer || "Unknown Brand"}
                          {suggestion.suggestedPackage ? ` · ${suggestion.suggestedPackage}` : ""}
                        </small>
                        <p>{suggestion.reason || item.matchReason}</p>
                      </div>
                      <div className="bom-ai-review-actions">
                        <button
                          type="button"
                          className="bom-ai-reject-button"
                          disabled={isReviewingAi}
                          onClick={() => handleAiReview(item._id, "reject")}
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          className="bom-ai-confirm-button"
                          disabled={isReviewingAi}
                          onClick={() => handleAiReview(item._id, "accept")}
                        >
                          Confirm Match
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {candidateReviewItem && (
            <section className="bom-candidate-panel" aria-label="Candidate matching confirmation">
              <div className="bom-candidate-heading">
                <div>
                  <strong>Candidate Review · Row {candidateReviewItem.lineNo}</strong>
                  <span>{candidateReviewItem.matchReason || "Search and select a replacement part"}</span>
                </div>
                <select
                  value={candidateReviewItem._id}
                  onChange={(event) => {
                    setCandidateReviewItemId(event.target.value);
                    setCandidateKeyword("");
                    setSearchedCandidates(null);
                  }}
                >
                  {(bomOrder.items || []).map((item) => (
                    <option key={item._id} value={item._id}>Row {item.lineNo} · {item.normalized?.originalMpn || item.normalized?.originalSupplierPartNo || "Unnamed item"}</option>
                  ))}
                </select>
              </div>
              <form className="bom-candidate-search" onSubmit={handleCandidateSearch}>
                <input
                  value={candidateKeyword}
                  onChange={(event) => setCandidateKeyword(event.target.value)}
                  placeholder="Search MPN, CTYEA Number, brand, or product name"
                />
                <button type="submit" disabled={isSearchingCandidates}>
                  {isSearchingCandidates ? "Searching..." : "Search Replacements"}
                </button>
              </form>
              {(candidateReviewItem.warnings || []).map((warning) => (
                <p key={warning} className="bom-candidate-warning">{warning}</p>
              ))}
              <div className="bom-candidate-list">
                {visibleCandidates.map((candidate) => (
                  <article key={candidate._id} className="bom-candidate-item">
                    <div className="bom-candidate-summary">
                      <span>Match score {candidate.matchScore ?? candidateReviewItem.confidence ?? 0}%</span>
                      <strong>{candidate.Manufacturer_Part_Number || candidate.CTYEA_Part_Number || candidate.name}</strong>
                      <small>{candidate.Manufacturer || "Unknown Brand"} · Stock {candidate.stock || 0}</small>
                    </div>
                    <div className="bom-candidate-diffs">
                      {(candidate.fieldDiffs || []).map((diff) => (
                        <span key={diff.field} className={`is-${diff.status}`} title={`Required: ${diff.expected || "-"}; Candidate: ${diff.actual || "-"}`}>
                          {diff.label}: {diff.status === "match" ? "Match" : diff.status === "partial" ? "Partial" : diff.status === "different" ? "Different" : "Not provided"}
                        </span>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="bom-ai-confirm-button"
                      disabled={isConfirmingCandidate}
                      onClick={() => handleConfirmCandidate(candidate._id)}
                    >
                      Confirm This Part
                    </button>
                  </article>
                ))}
                {visibleCandidates.length === 0 && <p className="bom-candidate-empty">No candidates found. Try another keyword.</p>}
              </div>
            </section>
          )}

          {(isLoadingPurchasePreview || purchasePreview?.groups?.length > 0) && (
            <section className="bom-purchase-preview" aria-label="Merged purchase preview">
              <div className="bom-purchase-preview-heading">
                <div>
                  <strong>Merged Purchase Preview</strong>
                  <span>Same products are merged before MOQ rounding, tier pricing, and stock validation.</span>
                </div>
                {isLoadingPurchasePreview && <em>Refreshing...</em>}
              </div>
              {purchasePreview?.summary && (
                <div className="bom-purchase-preview-summary">
                  <span>Required <strong>{purchasePreview.summary.requestedQuantity}</strong></span>
                  <span>Purchase <strong>{purchasePreview.summary.purchaseQuantity}</strong></span>
                  <span>MOQ Difference <strong>+{purchasePreview.summary.quantityDifference}</strong></span>
                  <span>Total <strong>${formatMoney(purchasePreview.summary.totalAmount)}</strong></span>
                </div>
              )}
              <div className="bom-purchase-preview-list">
                {(purchasePreview?.groups || []).map((group) => (
                  <article key={group.productId} className={(group.insufficientStock || group.needQuote) ? "has-error" : ""}>
                    <strong>{group.productLabel || group.product?.name}</strong>
                    <span>Rows {group.lineNos.join(", ")}</span>
                    <span>{group.requestedQuantity} required → {group.purchaseQuantity} purchased</span>
                    <span>MOQ {group.minimumMultiple} · {group.priceSource}</span>
                    <span>{group.needQuote ? "Requires quote" : `$${formatMoney(group.unitPrice)} / unit · $${formatMoney(group.subtotal)}`}</span>
                    <span>Stock {group.availableStock}{group.insufficientStock ? " · Insufficient" : ""}</span>
                  </article>
                ))}
              </div>
            </section>
          )}

          <section className="bom-univer-wrap">
            <UniverBomSheet
              bomOrder={bomOrder}
              items={filteredItems}
              columns={visibleColumns}
              onReady={handleUniverReady}
              onValueChanged={handleSheetValueChanged}
            />
          </section>

          <footer className="bom-sheet-footer">
            <div className="bom-footer-left">
              <button
                type="button"
                className="bom-trash-button"
                title="Delete item rows in the current spreadsheet selection"
                onClick={handleDeleteSelectedRows}
                disabled={isDeletingItems}
              >
                ×
              </button>
              <label>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={(event) => toggleVisibleSelection(event.target.checked)}
                />
                Select All
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={onlySelected}
                  onChange={(event) => setOnlySelected(event.target.checked)}
                />
                Selected Only
              </label>
              <span>{selectedItemIds.size} row(s) selected</span>
              <span>{summary.totalRows || 0} item(s) total</span>
            </div>
            <div className="bom-footer-summary">
              <span>{summary.totalRows || 0} row(s) total</span>
              <span>{summary.matchedRows || 0} exact match(es)</span>
              <span>{summary.pendingRows || 0} pending</span>
              <span>{summary.unmatchedRows || 0} unmatched</span>
            </div>
            <div className="bom-footer-actions">
              <div>
                <span>Total:</span>
                <strong>${formatMoney(purchaseTotalAmount)}</strong>
              </div>
              <button
                type="button"
                className="bom-primary-button"
                onClick={addSelectedToCart}
                disabled={isAdding || selectedItemIds.size === 0 || hasPurchaseBlockingIssue || hasUnsafePurchaseEdits}
                title={hasUnsafePurchaseEdits ? "Wait for all BOM row changes to save successfully before adding to cart" : ""}
              >
                {isAdding
                  ? "Adding..."
                  : (hasRowSaveErrors ? "Fix save errors" : (hasPendingRowChanges ? "Saving changes..." : "Add to Cart"))}
              </button>
            </div>
          </footer>
        </div>
      )}
    </main>
  );
};

export default BomOrderPage;
